import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { projectId, publicAnonKey } from './info';

// Declare global window extensions for TypeScript
declare global {
  interface Window {
    __ITINLUX_SUPABASE_CLIENT__?: SupabaseClient;
    __ITINLUX_CLIENT_CREATION_COUNT__?: number;
    __ITINLUX_CLIENT_LOCK__?: boolean;
  }
}

// Global client instance - null until first creation
let globalClient: SupabaseClient | null = null;

// Client creation function with strict singleton enforcement
function createSupabaseClient(): SupabaseClient {
  // Return existing client if available (priority: module-level > window-level)
  if (globalClient) {
    return globalClient;
  }

  // Check for window-level client (for hot reloads)
  if (typeof window !== 'undefined' && window.__ITINLUX_SUPABASE_CLIENT__) {
    globalClient = window.__ITINLUX_SUPABASE_CLIENT__;
    return globalClient;
  }

  // Prevent concurrent client creation
  if (typeof window !== 'undefined') {
    if (window.__ITINLUX_CLIENT_LOCK__) {
      // Wait for existing creation to complete
      let attempts = 0;
      const maxAttempts = 50; // 500ms max wait
      while (window.__ITINLUX_CLIENT_LOCK__ && attempts < maxAttempts) {
        attempts++;
        // Synchronous wait (not ideal but necessary for singleton)
        for (let i = 0; i < 1000000; i++) {} // Small delay
      }
      
      if (window.__ITINLUX_SUPABASE_CLIENT__) {
        globalClient = window.__ITINLUX_SUPABASE_CLIENT__;
        return globalClient;
      }
    }

    // Set creation lock
    window.__ITINLUX_CLIENT_LOCK__ = true;
    
    // Track creation count for debugging
    window.__ITINLUX_CLIENT_CREATION_COUNT__ = (window.__ITINLUX_CLIENT_CREATION_COUNT__ || 0) + 1;
    
    if (window.__ITINLUX_CLIENT_CREATION_COUNT__ > 1) {
      console.warn(`⚠️ Multiple client creation attempts detected! Count: ${window.__ITINLUX_CLIENT_CREATION_COUNT__}`);
    }
  }

  // Clear any conflicting storage before creating new client
  cleanupConflictingStorage();

  // Create the client with strict configuration
  globalClient = createClient(
    `https://${projectId}.supabase.co`, 
    publicAnonKey,
    {
      auth: {
        storageKey: 'sb-itinlux-auth-final', // Changed key to force clean state
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: typeof window !== 'undefined',
        flowType: 'pkce'
      },
      realtime: {
        params: {
          eventsPerSecond: 0,
        },
      },
      global: {
        headers: {
          'X-Client-Info': 'itinlux-singleton-v3',
          'X-Creation-Time': Date.now().toString()
        }
      }
    }
  );

  // Store in window for persistence across hot reloads
  if (typeof window !== 'undefined') {
    window.__ITINLUX_SUPABASE_CLIENT__ = globalClient;
    window.__ITINLUX_CLIENT_LOCK__ = false; // Release lock
  }

  // Log client creation in development
  if (process.env.NODE_ENV === 'development') {
    const creationCount = typeof window !== 'undefined' ? window.__ITINLUX_CLIENT_CREATION_COUNT__ : 1;
    if (creationCount === 1) {
      console.log('✅ Supabase client initialized (singleton pattern)');
    } else {
      console.warn(`⚠️ Additional client creation attempt #${creationCount} - this should not happen!`);
    }
  }

  return globalClient;
}

// Cleanup function to remove conflicting storage
function cleanupConflictingStorage(): void {
  if (typeof window === 'undefined') return;

  try {
    // List of all possible conflicting keys
    const conflictingKeys = [
      'sb-itinlux-web-auth-v2',
      'sb-itinlux-web-auth',
      'sb-auth-token',
      'supabase.auth.token',
      'supabase-session',
      'sb-session'
    ];

    let removedCount = 0;
    
    // Clean localStorage
    conflictingKeys.forEach(key => {
      if (localStorage.getItem(key)) {
        localStorage.removeItem(key);
        removedCount++;
      }
    });

    // Clean sessionStorage
    conflictingKeys.forEach(key => {
      if (sessionStorage.getItem(key)) {
        sessionStorage.removeItem(key);
        removedCount++;
      }
    });

    // Clean any remaining sb-* keys except our current one
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-') && key !== 'sb-itinlux-auth-final') {
        localStorage.removeItem(key);
        removedCount++;
      }
    });

    if (removedCount > 0 && process.env.NODE_ENV === 'development') {
      console.log(`🧹 Cleaned ${removedCount} conflicting storage keys`);
    }
  } catch (error) {
    console.warn('Failed to cleanup conflicting storage:', error);
  }
}

// Create the singleton instance immediately
const supabaseClient = createSupabaseClient();

// Export the singleton instance
export const supabase = supabaseClient;

// Export as default as well
export default supabaseClient;

// Export function to get client (always returns the same instance)
export function getSupabaseClient(): SupabaseClient {
  return supabaseClient;
}

// Export function to force reset (for development only)
export function resetSupabaseClient(): void {
  if (process.env.NODE_ENV !== 'development') {
    console.warn('resetSupabaseClient() is only available in development mode');
    return;
  }

  if (typeof window !== 'undefined') {
    delete window.__ITINLUX_SUPABASE_CLIENT__;
    delete window.__ITINLUX_CLIENT_CREATION_COUNT__;
    delete window.__ITINLUX_CLIENT_LOCK__;
  }
  
  globalClient = null;
  console.log('🔄 Supabase client completely reset');
}

// Development: Add window debugging functions
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as any).__debugSupabaseClient = () => {
    console.log('🔍 Supabase Client Debug Info:', {
      hasGlobalClient: !!globalClient,
      hasWindowClient: !!(window as any).__ITINLUX_SUPABASE_CLIENT__,
      creationCount: (window as any).__ITINLUX_CLIENT_CREATION_COUNT__ || 0,
      isLocked: !!(window as any).__ITINLUX_CLIENT_LOCK__,
      storageKeys: Object.keys(localStorage).filter(key => key.includes('sb-') || key.includes('supabase'))
    });
  };
}